import React, { useState } from "react";
import PropTypes from "prop-types";
import { withTheme } from "styled-components";
import styled from "styled-components";
import { Flex } from "rebass";
import { Choice } from "../Buttons";
import theme from "../../../themes";
import QuestionTitle from "./Title";
import QuestionIcon from "./QuestionIcon";
import Help from "./Help";
import { useTranslation } from "react-i18next";
import { getEnglishTranslation } from "../../../utils/englishI18n";
import BodyMap from "./BodyMap";

const SideBySideContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-self: center;
    width: 100%;
`;

const QuestionGridContainer = styled.div`
    flex-direction: column;
    justify-content: flex-start;
    align-self: center;
`;

const ChoicesColumn = styled.ul`
    display: flex;
    flex-direction: column;
    list-style: none;
    padding: 0;
    width: 100%;
    overflow-y: auto;
`;

// Matches the stacked Choice question: buttons wrap across the row, and don't shrink, so a scrolling
// container can't squash them to min-height and clip long labels or help text
const ChoicesStacked = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    max-width: 100%;
    padding: 1em 0;
    overflow: auto;

    > * {
        flex-shrink: 0;
    }
`;

const MultipleAnswer = ({ id, model, onAnswersChange }) => {
    const { t, i18n } = useTranslation();

    const handleSelect = text => {
        const updated = [...answers, text];
        setAnswers(updated);
        onAnswersChange(updated);
    };

    const handleDeselect = text => {
        const updated = answers.filter(a => a !== text);
        setAnswers(updated);
        onAnswersChange(updated);
    };

    const isBody = model.isDisplayOptionBody ? model.isDisplayOptionBody() : false;
    if (isBody) {
        return <BodyMap id={id} model={model} onAnswersChange={onAnswersChange} />;
    }

    const choices = model.getChoices();
    const title = model.getDisplayText();
    const iconType = model.getQuestionIcon();
    const helpText = model.getHelpText();
    const choiceSize = model.getChoiceSize ? model.getChoiceSize() || "large" : "large";
    const isSideBySide = model.isDisplayOptionSideBySide ? model.isDisplayOptionSideBySide() : false;
    const [answers, setAnswers] = useState([]);

    const choiceItems = choices.map((choice, index) => (
        <Choice
            key={index}
            size={choiceSize}
            selected={answers.some(a => a === getEnglishTranslation(choice.label, choice.label, i18n))}
            schema={choice}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
            isFirst={index === 0}
            isLast={index === choices.length - 1}
            isFullWidth={isSideBySide}
        />
    ));

    if (isSideBySide) {
        return (
            <SideBySideContainer>
                <QuestionGridContainer>
                    {iconType && <QuestionIcon type={theme.icons.types[iconType]} />}
                    <QuestionTitle htmlFor={id}>{t(title)}</QuestionTitle>
                    {helpText && <Help>{t(helpText)}</Help>}
                </QuestionGridContainer>
                <ChoicesColumn>
                    {choiceItems.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ChoicesColumn>
            </SideBySideContainer>
        );
    }

    return (
        <Flex alignItems="center" justifyContent={"flex-start"} flexDirection={"column"} overflow={"auto"}>
            {iconType && <QuestionIcon type={theme.icons.types[iconType]} />}
            <QuestionTitle htmlFor={id}>{t(title)}</QuestionTitle>
            {helpText && <Help>{t(helpText)}</Help>}
            <ChoicesStacked>{choiceItems}</ChoicesStacked>
        </Flex>
    );
};

MultipleAnswer.propTypes = {
    id: PropTypes.any,
    model: PropTypes.object,
    onAnswersChange: PropTypes.func
};

export default withTheme(MultipleAnswer);
